import { useScrollLock } from "@/hooks/useScrollLock";
import { feedbackApi, FeedbackRequest } from "@/services/api/feedback";
import { MessageOutlined } from "@ant-design/icons";
import { Button, Form, Input, message, Modal } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type FeedbackModalProps = {
  open: boolean;
  onClose: () => void;
};

// 主题色彩
const colors = {
  primary: "#00B4B4",
  primaryDark: "#008B8B",
  primaryLight: "#6BCFA3",
  text: "#2d3748",
  textLight: "#4a5568",
  textMuted: "#718096",
  textSecondary: "#4a5568",
  border: "#e2e8f0",
  background: "#ffffff",
  backgroundGlass: "rgba(255, 255, 255, 0.25)",
};

// 动画关键帧
const keyframes = `
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-10px) rotate(2deg); }
  }

  @keyframes glow {
    0%, 100% { box-shadow: 0 0 20px rgba(0, 180, 180, 0.3); }
    50% { box-shadow: 0 0 40px rgba(0, 180, 180, 0.6), 0 0 60px rgba(0, 128, 128, 0.4); }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }

  @keyframes slideInUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @keyframes particleFloat {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    25% { transform: translateY(-15px) translateX(5px); }
    50% { transform: translateY(-10px) translateX(-5px); }
    75% { transform: translateY(-20px) translateX(3px); }
  }

  @keyframes successBounce {
    0% { transform: scale(0.3); opacity: 0; }
    50% { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }

  @keyframes successGlow {
    0%, 100% {
      box-shadow: 0 0 20px rgba(0, 180, 180, 0.2);
    }
    50% {
      box-shadow: 0 0 40px rgba(0, 180, 180, 0.3), 0 0 60px rgba(0, 128, 128, 0.2);
    }
  }
`;

const styles = {
  modalContent: {
    borderRadius: "6px",
    padding: "0",
    background: `
      linear-gradient(135deg,
        rgba(255, 255, 255, 0.95) 0%,
        rgba(255, 255, 255, 0.85) 50%,
        rgba(255, 255, 255, 0.95) 100%
      )
    `,
    backdropFilter: "blur(20px) saturate(180%)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    boxShadow: `
      0 32px 64px rgba(0, 0, 0, 0.15),
      0 16px 32px rgba(0, 180, 180, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.6)
    `,
    overflow: "hidden",
    position: "relative" as const,
  },

  backgroundDecoration: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 20%, rgba(0, 180, 180, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(0, 139, 139, 0.06) 0%, transparent 50%),
      radial-gradient(circle at 40% 60%, rgba(63, 213, 199, 0.05) 0%, transparent 50%)
    `,
    zIndex: 0,
  },

  contentContainer: {
    position: "relative" as const,
    zIndex: 1,
    padding: "32px",
    background: "#ffffff",
  },

  header: {
    textAlign: "center" as const,
    marginBottom: "32px",
    animation: "slideInUp 0.8s ease-out",
  },

  icon: {
    width: "70px",
    height: "70px",
    margin: "0 auto 20px",
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    color: "white",
    boxShadow: `0 8px 32px rgba(0, 180, 180, 0.3)`,
    animation: "float 3s ease-in-out infinite",
  },

  title: {
    backgroundImage: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 50%, ${colors.primaryLight} 100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    fontSize: "24px",
    fontWeight: 700,
    marginBottom: "8px",
    letterSpacing: "-0.5px",
  },

  subtitle: {
    color: colors.textMuted,
    fontSize: "14px",
    fontWeight: 500,
    opacity: 0.8,
  },

  formContainer: {
    marginTop: "24px",
    animation: "slideInUp 0.8s ease-out 0.2s both",
  },

  formLabel: {
    fontWeight: 600,
    color: colors.text,
    fontSize: "16px",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  textarea: {
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.backgroundGlass,
    backdropFilter: "blur(10px)",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    fontSize: "15px",
    fontWeight: 500,
    minHeight: "140px",
    resize: "vertical" as const,
    padding: "16px",
  },

  submitButton: {
    height: "56px",
    borderRadius: "60px",
    fontSize: "16px",
    fontWeight: 700,
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
    backgroundSize: "200% 200%",
    backgroundPosition: "0% 0%",
    border: "none",
    color: "white",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: `0 12px 40px rgba(0, 180, 180, 0.2)`,
    marginTop: "20px",
    position: "relative" as const,
    overflow: "hidden",
  },

  submitButtonHover: {
    transform: "translateY(-3px) scale(1.02)",
    boxShadow: `0 20px 60px rgba(0, 180, 180, 0.4)`,
    backgroundPosition: "100% 0",
  },

  submitButtonDisabled: {
    background: "linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)",
    color: colors.textMuted,
    transform: "none",
    boxShadow: "none",
    cursor: "not-allowed",
  },
};

// 自定义样式
const customStyles = `
  .feedback-input:focus {
    border-color: ${colors.primary} !important;
    box-shadow: 0 0 0 4px rgba(0, 180, 180, 0.1) !important;
    background-color: rgba(255, 255, 255, 0.4) !important;
    transform: translateY(-2px);
  }

  /* 统一右上角关闭按钮样式（与 Login/ContactUs 保持一致） */
  .feedback-modal-custom .ant-modal-close {
    right: 16px;
    top: 16px;
    color: ${colors.textSecondary};
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  .feedback-modal-custom .ant-modal-close:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
  }
`;

// 粒子背景组件
const ParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
      color: string;
    }> = [];

    for (let i = 0; i < 20; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 2 + 1,
        opacity: Math.random() * 0.4 + 0.1,
        color: Math.random() > 0.5 ? "#00B4B4" : "#6b7280",
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      particles.forEach((particle, index) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.offsetWidth)
          particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.offsetHeight)
          particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle =
          particle.color +
          Math.floor(particle.opacity * 255)
            .toString(16)
            .padStart(2, "0");
        ctx.fill();

        particles.forEach((otherParticle, otherIndex) => {
          if (index !== otherIndex) {
            const dx = particle.x - otherParticle.x;
            const dy = particle.y - otherParticle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 80) {
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              ctx.strokeStyle =
                "#00B4B4" +
                Math.floor((1 - distance / 80) * 30)
                  .toString(16)
                  .padStart(2, "0");
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        });
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        opacity: 0.4,
      }}
    />
  );
};

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  // const { message } = App.useApp()
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // 防止移动端滚动穿透
  useScrollLock(open);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const payload: FeedbackRequest = {
        content: values.content,
      };

      await feedbackApi.submit(payload);

      setSubmitted(true);
      message.success(t("common.modals.feedback_success_desc"));

      // 2秒后关闭模态框
      setTimeout(() => {
        setLoading(false);
        setSubmitted(false);
        form.resetFields();
        onClose();
      }, 2000);
    } catch (error: any) {
      console.error("[FeedbackModal] submit error", error);
      message.error(error?.message || t("common.modals.feedback_error"));
      setLoading(false);
    }
  };

  // 注入自定义样式和动画
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = keyframes + customStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const textareaRef = useRef<any>(null);

  useEffect(() => {
    if (open) {
      // 微延迟，确保内容挂载后再聚焦
      const t = setTimeout(() => {
        try {
          textareaRef.current?.focus?.();
          // 移动端确保视口定位并唤起软键盘更友好
          textareaRef.current?.scrollIntoView?.({
            behavior: "smooth",
            block: "center",
          });
        } catch {}
      }, 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      styles={{
        content: styles.modalContent,
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(8px)",
        },
      }}
      destroyOnHidden
      centered
      rootClassName="feedback-modal-custom"
    >
      {/* 背景装饰 */}
      <div style={styles.backgroundDecoration} />

      {/* 粒子背景 */}
      <ParticleBackground />

      {/* 主要内容 */}
      <div style={styles.contentContainer}>
        {/* 头部区域 */}
        <div style={styles.header}>
          <div style={styles.icon}>
            <MessageOutlined />
          </div>
          <div style={styles.title}>{t("navbar.menu_feedback")}</div>
          <div style={styles.subtitle}>
            {t("common.modals.feedback_value_intro")}
          </div>
        </div>

        {submitted ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              background: `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.primaryDark}10 100%)`,
              borderRadius: "6px",
              border: `1px solid ${colors.primary}20`,
            }}
          >
            <div
              style={{
                fontSize: "48px",
                color: colors.primary,
                marginBottom: "16px",
                animation:
                  "successBounce 0.8s ease-out, successGlow 2s ease-in-out infinite",
              }}
            >
              ✓
            </div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: 600,
                color: colors.text,
                marginBottom: "8px",
              }}
            >
              {t("common.modals.feedback_success_title")}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: colors.textMuted,
              }}
            >
              {t("common.modals.feedback_support_thanks")}
            </div>
          </div>
        ) : (
          <div style={styles.formContainer}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item
                label={
                  <span style={styles.formLabel}>
                    <MessageOutlined />
                    {t("common.modals.feedback_label")}
                  </span>
                }
                name="content"
                rules={[
                  {
                    required: true,
                    message: t("common.modals.feedback_required"),
                  },
                  { min: 10, message: t("common.modals.feedback_min_length") },
                  {
                    max: 1000,
                    message: t("common.modals.feedback_max_length"),
                  },
                ]}
              >
                <Input.TextArea
                  ref={textareaRef}
                  placeholder={t("common.modals.feedback_placeholder")}
                  maxLength={1000}
                  showCount
                  className="feedback-input"
                  style={styles.textarea}
                  rows={6}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
                style={styles.submitButton}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform =
                      "translateY(-3px) scale(1.02)";
                    e.currentTarget.style.boxShadow =
                      "0 20px 60px rgba(0, 180, 180, 0.4)";
                    e.currentTarget.style.backgroundPosition = "100% 0";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow =
                      "0 12px 40px rgba(0, 180, 180, 0.2)";
                    e.currentTarget.style.backgroundPosition = "0% 0%";
                  }
                }}
              >
                {t("common.modals.feedback_submit_btn")}
              </Button>
            </Form>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FeedbackModal;
